import {json, type LinksFunction, type MetaFunction} from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
} from '@remix-run/react';

import {captureRemixErrorBoundaryError} from '@sentry/remix';
import clsx from 'clsx';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {PrimeReactProvider} from 'primereact/api';
import faviconHref from '~/assets/imgs/logo.svg?url';
import Header from './components/Header';
import useIsInIframe from './hooks/useIsInIframe';
import useSendHeight from './hooks/useSendHeight';
import appStylesHref from './styling/app.css?url';
import * as gtag from '~/utils/gtags.client';
import {useEffect} from 'react';
import {GA_TRACKING_ID} from './utils/environment';

dayjs.extend(localizedFormat);

export const loader = async () => {
  return json({gaTrackingId: GA_TRACKING_ID});
};

const HeadSection = (props: React.PropsWithChildren) => (
  <head>
    <meta charSet="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta httpEquiv="Access-Control-Allow-Origin" content="*" />
    <meta
      name="description"
      content="Thank you for trusting ATS when moving your precious freight. Real-time tracking and documentation are also available using our Customer Portal."></meta>
    {props.children}
    <Meta />
    <Links />
  </head>
);

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <html lang="en">
      <HeadSection />
      <body>
        <div>Something went wrong</div>
      </body>
    </html>
  );
};

export const meta: MetaFunction = () => {
  return [{title: 'Load Tracker'}];
};

export default function App() {
  const isInIframe = useIsInIframe();
  useSendHeight(isInIframe);
  const location = useLocation();
  const {gaTrackingId} = useLoaderData<typeof loader>();

  useEffect(() => {
    if (gaTrackingId) {
      gtag?.pageview(location.pathname, gaTrackingId);
    }
  }, [location.pathname, gaTrackingId]);

  return (
    <html lang="en">
      <HeadSection>
        {gaTrackingId && (
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`}></script>
        )}
        {gaTrackingId && (
          <script
            async
            id="gtag-init"
            dangerouslySetInnerHTML={{
              __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', '${gaTrackingId}', {
              page_path: window.location.pathname,
            });
          `,
            }}
          />
        )}
      </HeadSection>
      <body className={clsx('flex-col', isInIframe && 'overflow-hidden')}>
        {!isInIframe && <Header />}
        <main>
          <PrimeReactProvider>
            <Outlet />
          </PrimeReactProvider>
        </main>
        <Scripts />
        <ScrollRestoration />
      </body>
    </html>
  );
}

export const links: LinksFunction = () => [
  {rel: 'stylesheet', href: appStylesHref},
  {rel: 'icon', href: faviconHref},
];
